import axios from '@/plugins/axios';

class WhatsappService {
  /**
   * Busca todas as instâncias de WhatsApp
   * @returns {Promise} Promessa com a lista de instâncias
   */
  async getAllInstances() {
    try {
      const response = await axios.get('/whatsapp/instances');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar instâncias de WhatsApp:', error);
      throw error;
    }
  }

  /**
   * Busca uma instância de WhatsApp pelo ID
   * @param {number} instanceId - ID da instância
   * @returns {Promise} Promessa com os detalhes da instância
   */
  async getInstance(instanceId) {
    try {
      const response = await axios.get(`/whatsapp/instances/${instanceId}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar instância de WhatsApp ${instanceId}:`, error);
      throw error;
    }
  }

  /**
   * Cria uma nova instância de WhatsApp
   * @param {Object} instanceData - Dados da instância
   * @returns {Promise} Promessa com os detalhes da instância criada
   */
  async createInstance(instanceData) {
    try {
      const response = await axios.post('/whatsapp/instances', instanceData);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar instância de WhatsApp:', error);
      throw error;
    }
  }

  /**
   * Conecta uma instância de WhatsApp
   * @param {number} instanceId - ID da instância
   * @returns {Promise} Promessa com os detalhes da conexão
   */
  async connectInstance(instanceId) {
    try {
      const response = await axios.post(`/whatsapp/instances/${instanceId}/connect`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao conectar instância de WhatsApp ${instanceId}:`, error);
      throw error;
    }
  }

  /**
   * Verifica o estado da conexão de uma instância
   * @param {number} instanceId - ID da instância
   * @returns {Promise} Promessa com o estado da conexão
   */
  async getConnectionState(instanceId) {
    try {
      const response = await axios.get(`/whatsapp/instances/${instanceId}/state`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao verificar estado da conexão da instância ${instanceId}:`, error);
      throw error;
    }
  }

  /**
   * Desconecta uma instância de WhatsApp
   * @param {number} instanceId - ID da instância
   * @returns {Promise} Promessa com o resultado da desconexão
   */
  async logoutInstance(instanceId) {
    try {
      const response = await axios.post(`/whatsapp/instances/${instanceId}/logout`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao desconectar instância de WhatsApp ${instanceId}:`, error);
      throw error;
    }
  }

  /**
   * Exclui uma instância de WhatsApp
   * @param {number} instanceId - ID da instância
   * @returns {Promise} Promessa com o resultado da exclusão
   */
  async deleteInstance(instanceId) {
    try {
      const response = await axios.delete(`/whatsapp/instances/${instanceId}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao excluir instância de WhatsApp ${instanceId}:`, error);
      throw error;
    }
  }

  /**
   * Configura o webhook de uma instância
   * @param {number} instanceId - ID da instância
   * @param {Object} webhookData - Dados do webhook
   * @returns {Promise} Promessa com o resultado da configuração
   */
  async setWebhook(instanceId, webhookData) {
    try {
      const response = await axios.post(`/whatsapp/instances/${instanceId}/webhook`, webhookData);
      return response.data;
    } catch (error) {
      console.error(`Erro ao configurar webhook da instância ${instanceId}:`, error);
      throw error;
    }
  }

  /**
   * Configura as configurações de uma instância
   * @param {number} instanceId - ID da instância
   * @param {Object} settings - Configurações da instância
   * @returns {Promise} Promessa com o resultado da configuração
   */
  async setSettings(instanceId, settings) {
    try {
      const response = await axios.post(`/whatsapp/instances/${instanceId}/settings`, settings);
      return response.data;
    } catch (error) {
      console.error(`Erro ao configurar settings da instância ${instanceId}:`, error);
      throw error;
    }
  }

  /**
   * Envia uma mensagem de texto
   * @param {number} instanceId - ID da instância
   * @param {Object} messageData - Dados da mensagem
   * @returns {Promise} Promessa com o resultado do envio
   */
  async sendMessage(instanceId, messageData) {
    try {
      const response = await axios.post(`/whatsapp/instances/${instanceId}/messages`, messageData);
      return response.data;
    } catch (error) {
      console.error(`Erro ao enviar mensagem pela instância ${instanceId}:`, error);
      throw error;
    }
  }

  /**
   * Envia uma mensagem com mídia
   * @param {number} instanceId - ID da instância
   * @param {Object} mediaData - Dados da mensagem com mídia
   * @returns {Promise} Promessa com o resultado do envio
   */
  async sendMediaMessage(instanceId, mediaData) {
    try {
      const response = await axios.post(`/whatsapp/instances/${instanceId}/messages/media`, mediaData);
      return response.data;
    } catch (error) {
      console.error(`Erro ao enviar mensagem com mídia pela instância ${instanceId}:`, error);
      throw error;
    }
  }

  /**
   * Busca todos os chats de uma instância
   * @param {number} instanceId - ID da instância
   * @returns {Promise} Promessa com a lista de chats
   */
  async getChats(instanceId) {
    try {
      const response = await axios.get(`/whatsapp/instances/${instanceId}/chats`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar chats da instância ${instanceId}:`, error);
      throw error;
    }
  }

  /**
   * Busca as mensagens de um chat
   * @param {number} instanceId - ID da instância
   * @param {number} chatId - ID do chat
   * @returns {Promise} Promessa com a lista de mensagens
   */
  async getChatMessages(instanceId, chatId) {
    try {
      const response = await axios.get(`/whatsapp/instances/${instanceId}/chats/${chatId}/messages`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar mensagens do chat ${chatId} da instância ${instanceId}:`, error);
      throw error;
    }
  }
}

export default new WhatsappService(); 